import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useState } from 'react'
import { AlertTriangleIcon } from 'lucide-react'
import {
	Alert, AlertDescription, AlertTitle,
	AuthCaption,
	AuthHeading,
	AuthLayout,
	AuthLeftBlock,
	AuthRightBlock,
	Button,
	Checkbox,
	Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage,
	RadioCard,
	Stepper,
} from '@rentflowdev/ui'
import { useUpdateTaxRegimeMutation } from '@/api'
import { extractErrorMessage } from '@/lib/error-message'
import { zodResolverI18n } from '@/lib/zod-resolver-i18n'
import { USN_INCOME_BANDS } from '@/types'

export const Route = createFileRoute('/onboarding/tax-regime')({
	component: TaxRegimePage,
})

/**
 * Strict subset of Prisma `Account`: taxRegime, usnIncomeBand, vatExemptArt145,
 * vatVoluntary22. NONE means "not stated" and INDIVIDUAL is set for an
 * individual account at step 1, so neither is offered here.
 */
const REGIMES = ['OSNO', 'USN', 'AUSN', 'PSN', 'NPD'] as const

const taxRegimeSchema = z
	.object({
		taxRegime:       z.enum(REGIMES, { errorMap: () => ({ message: 'onboarding.taxRegime.required' }) }),
		usnIncomeBand:   z.enum(USN_INCOME_BANDS).optional(),
		vatExemptArt145: z.boolean().optional(),
		vatVoluntary22:  z.boolean().optional(),
	})
	// Mirrors the server: USN resolves no VAT rate without one of the two.
	.superRefine((values, ctx) => {
		if (values.taxRegime === 'USN' && !values.usnIncomeBand && !values.vatVoluntary22) {
			ctx.addIssue({
				code:    z.ZodIssueCode.custom,
				path:    ['usnIncomeBand'],
				message: 'settings.profile.taxation.bandRequired',
			})
		}
	})

type TaxRegimeFormValues = z.infer<typeof taxRegimeSchema>

function TaxRegimePage() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const [updateTaxRegime, { isLoading }] = useUpdateTaxRegimeMutation()
	const [submitError, setSubmitError] = useState<string | null>(null)

	const form = useForm<TaxRegimeFormValues>({
		resolver: zodResolverI18n<TaxRegimeFormValues>(taxRegimeSchema),
		defaultValues: { vatExemptArt145: false, vatVoluntary22: false },
		mode: 'onChange',
	})

	const regime = useWatch({ control: form.control, name: 'taxRegime' })
	const voluntary = useWatch({ control: form.control, name: 'vatVoluntary22' })

	const onSubmit = async (values: TaxRegimeFormValues) => {
		setSubmitError(null)
		try {
			await updateTaxRegime({
				taxRegime:       values.taxRegime,
				usnIncomeBand:   values.taxRegime === 'USN' && !values.vatVoluntary22 ? values.usnIncomeBand : undefined,
				vatExemptArt145: values.taxRegime === 'OSNO' ? (values.vatExemptArt145 ?? false) : false,
				vatVoluntary22:  values.taxRegime === 'USN' ? (values.vatVoluntary22 ?? false) : false,
			}).unwrap()
			void navigate({ to: '/onboarding/profile' })
		} catch (err) {
			setSubmitError(extractErrorMessage(err) ?? t('onboarding.taxRegime.saveError'))
		}
	}

	return (
		<AuthLayout>
			<AuthLeftBlock />

			<AuthRightBlock>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col w-full">
						<Stepper current={2} total={3} className="min-h-15" />

						<div className="flex flex-col gap-1">
							<AuthHeading className="text-left">{t('onboarding.taxRegime.title')}</AuthHeading>
							<AuthCaption className="text-left text-base leading-6">
								{t('onboarding.taxRegime.subtitle')}
							</AuthCaption>
						</div>

						<div className="flex flex-col gap-4 py-6">
							<FormField
								control={form.control}
								name="taxRegime"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="sr-only">{t('onboarding.taxRegime.title')}</FormLabel>
										<FormControl>
											<div role="radiogroup" className="flex flex-col gap-3">
												{REGIMES.map((r) => (
													<RadioCard
														key={r}
														title={t(`settings.profile.taxation.regimes.${r}`)}
														description={t(`settings.profile.taxation.regimeHints.${r}`)}
														selected={field.value === r}
														onClick={() => field.onChange(r)}
														onKeyDown={(e) => {
															if (e.key === 'Enter' || e.key === ' ') {
																e.preventDefault()
																field.onChange(r)
															}
														}}
													/>
												))}
											</div>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							{regime === 'NPD' && (
								<Alert variant="warning">
									<AlertTriangleIcon className="size-4" />
									<AlertTitle>{t('onboarding.taxRegime.npdWarningTitle')}</AlertTitle>
									<AlertDescription>{t('onboarding.taxRegime.npdWarning')}</AlertDescription>
								</Alert>
							)}

							{regime === 'USN' && (
								<section className="flex flex-col gap-3 border-t border-(--color-border) pt-4">
									{/* Bands stop mattering once the standard rate is elected. */}
									{!voluntary && (
										<FormField
											control={form.control}
											name="usnIncomeBand"
											render={({ field }) => (
												<FormItem>
													<FormLabel>{t('settings.profile.taxation.incomeBand')}</FormLabel>
													<FormControl>
														<div role="radiogroup" className="flex flex-col gap-3">
															{USN_INCOME_BANDS.map((b) => (
																<RadioCard
																	key={b}
																	title={t(`settings.profile.taxation.bands.${b}`)}
																	selected={field.value === b}
																	onClick={() => field.onChange(b)}
																	onKeyDown={(e) => {
																		if (e.key === 'Enter' || e.key === ' ') {
																			e.preventDefault()
																			field.onChange(b)
																		}
																	}}
																/>
															))}
														</div>
													</FormControl>
													<FormDescription>{t('settings.profile.taxation.bandHint')}</FormDescription>
													<FormMessage />
												</FormItem>
											)}
										/>
									)}
									<FormField
										control={form.control}
										name="vatVoluntary22"
										render={({ field }) => (
											<FormItem className="flex flex-row items-start gap-2 space-y-0">
												<FormControl>
													<Checkbox
														checked={field.value ?? false}
														onCheckedChange={(checked) => {
															field.onChange(checked === true)
															void form.trigger('usnIncomeBand')
														}}
													/>
												</FormControl>
												<div className="flex flex-col gap-0.5">
													<FormLabel className="font-[400]">{t('settings.profile.taxation.voluntary22')}</FormLabel>
													<FormDescription>{t('settings.profile.taxation.voluntary22Hint')}</FormDescription>
												</div>
											</FormItem>
										)}
									/>
								</section>
							)}

							{regime === 'OSNO' && (
								<FormField
									control={form.control}
									name="vatExemptArt145"
									render={({ field }) => (
										<FormItem className="flex flex-row items-center gap-2 space-y-0 border-t border-(--color-border) pt-4">
											<FormControl>
												<Checkbox
													checked={field.value ?? false}
													onCheckedChange={(checked) => field.onChange(checked === true)}
												/>
											</FormControl>
											<FormLabel className="font-[400]">{t('settings.profile.taxation.exemptArt145')}</FormLabel>
										</FormItem>
									)}
								/>
							)}

							{submitError && <p className="text-sm text-(--color-destructive)">{submitError}</p>}
						</div>

						<div className="flex gap-2">
							<Button
								type="button"
								variant="outline"
								className="flex-1"
								onClick={() => void navigate({ to: '/onboarding/account-type' })}
								disabled={isLoading}
							>
								{t('common.back')}
							</Button>
							<Button type="submit" className="flex-1" loading={isLoading} disabled={!form.formState.isValid}>
								{t('common.next')}
							</Button>
						</div>
					</form>
				</Form>
			</AuthRightBlock>
		</AuthLayout>
	)
}
